/**
 * HotspotGuardToggle — Stream H (R4)
 *
 * Pro-Hotspot-Schalter "Hotspot Guard" plus PoC-Benachrichtigung, wie im
 * v1-Monolithen (hotspots/_guard.haml). Premium-Feature: das Backend liefert
 * guard_available=false fuer Free-Accounts und antwortet auf einen Schreibversuch
 * mit 403 { error: { code: 'premium_required' } }. Beides wird hier behandelt —
 * der Schalter darf nicht stumm wirken.
 *
 * WICHTIG (Begriffe getrennt halten): `guard_enabled` ist der NUTZER-Schalter,
 * `guard_status` das Ergebnis des v2-Guard-Evaluators. Beide erscheinen auf der
 * Detailseite und sind deshalb hier explizit unterschiedlich beschriftet.
 */
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from '../../components/Toast/useToast'
import { useUpdateHotspot, type UpdateHotspotInput } from './useUpdateHotspot'
import { activeBcp47 } from '../../lib/locale'
import { isPremiumRequiredError } from '../../lib/premiumError'
import type { HotspotDetail } from './useHotspotDetail'

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleString(activeBcp47(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

// --- Upsell-Dialog ---

function UpsellDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('hotspots')
  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guard-upsell-title"
      data-testid="guard-upsell-dialog"
    >
      <div className="modal">
        <div className="modal-header">
          <h2 id="guard-upsell-title">{t('guard.premiumTitle')}</h2>
        </div>
        <div className="modal-body">
          <p>{t('guard.premiumRequired')}</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('guard.premiumDismiss')}
          </button>
          <Link to="/plans" className="btn btn-primary" onClick={onClose}>
            {t('guard.premiumCta')}
          </Link>
        </div>
      </div>
    </div>
  )
}

// --- Props ---

type Props = {
  hotspotId: string | number
  hotspot: HotspotDetail
}

export function HotspotGuardToggle({ hotspotId, hotspot }: Props) {
  const { t } = useTranslation('hotspots')
  const update = useUpdateHotspot(hotspotId)
  const [showUpsell, setShowUpsell] = useState(false)

  const guardEnabled = hotspot.guard_enabled === true
  const notifyPoc = hotspot.notify_poc === true
  // undefined (aelterer BE-Stand) wird als "verfuegbar" behandelt; nur ein
  // explizites false sperrt die Bedienung.
  const available = hotspot.guard_available !== false
  const mutedUntil = formatDateTime(hotspot.guard_muted_until)
  const isMuted = hotspot.guard_muted === true && mutedUntil != null

  async function submit(patch: UpdateHotspotInput) {
    try {
      await update.mutateAsync(patch)
    } catch (err) {
      if (isPremiumRequiredError(err)) {
        setShowUpsell(true)
        return
      }
      toast.error(t('guard.updateFailed'))
    }
  }

  function handleGuardToggle() {
    if (!available) {
      setShowUpsell(true)
      return
    }
    // guard ohne guard_muted_until senden — das BE setzt die Stummschaltung
    // dann selbst auf null (v1-Verhalten).
    void submit({ guard: !guardEnabled })
  }

  function handleUnmute() {
    void submit({ guard: true, guard_muted_until: null })
  }

  function handleNotifyPocToggle() {
    void submit({ notify_poc: !notifyPoc })
  }

  return (
    <div className="card card-pad" aria-label={t('guard.cardLabel')} data-testid="hotspot-guard-card">
      <h3 className="section-heading" style={{ marginBottom: 4 }}>
        {t('guard.heading')}
      </h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>
        {t('guard.description')}
      </p>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          padding: '8px 0',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <label htmlFor="hotspot-guard-switch" style={{ fontSize: 13 }}>
          {t('guard.toggleLabel')}
        </label>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span className={`pill ${guardEnabled ? 'pill-success' : ''}`} data-testid="guard-state-pill">
            {guardEnabled ? t('guard.stateEnabled') : t('guard.stateDisabled')}
          </span>
          <button
            id="hotspot-guard-switch"
            type="button"
            className="btn btn-sm"
            role="switch"
            aria-checked={guardEnabled}
            disabled={update.isPending}
            onClick={handleGuardToggle}
            data-testid="guard-toggle-button"
          >
            {guardEnabled ? t('guard.toggleDisable') : t('guard.toggleEnable')}
          </button>
        </span>
      </div>

      {isMuted && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '8px 0',
            borderBottom: '1px solid var(--border)',
          }}
          data-testid="guard-muted-row"
        >
          <span className="muted" style={{ fontSize: 12 }}>
            {t('guard.mutedUntil', { date: mutedUntil })}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={update.isPending}
            onClick={handleUnmute}
          >
            {t('guard.unmute')}
          </button>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          padding: '8px 0',
        }}
      >
        <label htmlFor="hotspot-notify-poc-switch" style={{ fontSize: 13 }}>
          {t('guard.notifyPoc')}
        </label>
        <button
          id="hotspot-notify-poc-switch"
          type="button"
          className="btn btn-sm"
          role="switch"
          aria-checked={notifyPoc}
          disabled={update.isPending}
          onClick={handleNotifyPocToggle}
          data-testid="notify-poc-toggle-button"
        >
          {notifyPoc ? t('guard.toggleDisable') : t('guard.toggleEnable')}
        </button>
      </div>

      {!available && (
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }} data-testid="guard-premium-hint">
          {t('guard.premiumHint')}
        </p>
      )}

      {showUpsell && <UpsellDialog onClose={() => setShowUpsell(false)} />}
    </div>
  )
}
